import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllEmployees } from '../../store/slices/employeeManagementSlice';
import EmployeeCard from './components/EmployeeCard';
import './KnowMyelinners.scss';

const KnowMyelinners = () => {
  const dispatch = useDispatch();
  const { employees, loading, error } = useSelector(state => state.employeeManagement);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKnowMore = (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmployee(null);
  };

  // Remove duplicates first (based on empID or email)
  const uniqueEmployees = employees.filter((emp, index, arr) => {
    // Remove entries with no name/email (blank "User" cards)
    if (!emp.name && !emp.displayName && !emp.email) {
      return false;
    }
    
    // Only show employees with serial field
    if (!emp.serial) {
      return false;
    }
    
    // Find first occurrence of this employee (by empID or email)
    const firstIndex = arr.findIndex(e => 
      (e.empID && emp.empID && e.empID === emp.empID) ||
      (e.email && emp.email && e.email === emp.email)
    );
    
    // Keep only the first occurrence
    return index === firstIndex;
  });

  // Filter employees based only on search term
  const filteredEmployees = uniqueEmployees.filter(emp => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const nameMatch = 
        (emp.displayName?.toLowerCase().includes(term)) || 
        (emp.firstName?.toLowerCase().includes(term)) || 
        (emp.lastName?.toLowerCase().includes(term)) ||
        (emp.name?.toLowerCase().includes(term));
      const emailMatch = emp.email?.toLowerCase().includes(term);
      
      if (!nameMatch && !emailMatch) return false;
    }
    
    return true;
  });

  // Sort employees by serial (all employees have serial at this point)
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    return a.serial - b.serial;
  });

  return (
    <div className="know-myelinners-container">
      <div className="page-header">
        <h1>Know Myelinners</h1>
        <p>Learn more about your colleagues at MyelinZ</p>
      </div>

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        
        <button 
          className="refresh-btn" 
          onClick={() => dispatch(fetchAllEmployees())}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading employees...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => dispatch(fetchAllEmployees())}>Try Again</button>
        </div>
      ) : sortedEmployees.length === 0 ? (
        <div className="no-results">
          <p>No employees found matching your criteria.</p>
        </div>
      ) : (
        <div className="employee-cards-grid">
          {sortedEmployees.map(employee => (
            <EmployeeCard 
              key={employee.id} 
              employee={employee}
              onKnowMore={() => handleKnowMore(employee)}
            />
          ))}
        </div>
      )}

      {/* Employee Detail Modal */}
      {showModal && selectedEmployee && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button className="close-btn" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="employee-detail">
                <div className="employee-detail-image">
                  {selectedEmployee.img ? (
                    <img src={selectedEmployee.img} alt={selectedEmployee.name || selectedEmployee.displayName} className="profile-image-large" />
                  ) : (
                    <div className="avatar-placeholder-large">
                      {(selectedEmployee.name || selectedEmployee.displayName || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                
                <div className="employee-detail-info">
                  <h2>{selectedEmployee.name || selectedEmployee.displayName || 'Unknown'}</h2>
                  {(selectedEmployee.designation || selectedEmployee.empID) && (
                    <p className="designation-detail">
                      {selectedEmployee.designation && selectedEmployee.empID 
                        ? `${selectedEmployee.designation} • ${selectedEmployee.empID}`
                        : selectedEmployee.designation || selectedEmployee.empID}
                    </p>
                  )}
                  
                  {(selectedEmployee.shortDescription || selectedEmployee.publicDescription || selectedEmployee.bio) && (
                    <div className="bio-detail">
                      <h4>About</h4>
                      <div dangerouslySetInnerHTML={{ 
                        __html: selectedEmployee.shortDescription || selectedEmployee.publicDescription || selectedEmployee.bio 
                      }} />
                    </div>
                  )}
                  
                  {(selectedEmployee.email || selectedEmployee.phone || selectedEmployee.location) && (
                    <div className="contact-detail">
                      <h4>Contact Information</h4>
                      <div className="contact-items">
                        {selectedEmployee.email && (
                          <div className="contact-item">
                            <strong>Email:</strong> {selectedEmployee.email}
                          </div>
                        )}
                        {selectedEmployee.phone && (
                          <div className="contact-item">
                            <strong>Phone:</strong> {selectedEmployee.phone}
                          </div>
                        )}
                        {selectedEmployee.location && (
                          <div className="contact-item">
                            <strong>Location:</strong> {selectedEmployee.location}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {(selectedEmployee.socialLinkedIn || selectedEmployee.socialTwitter || selectedEmployee.socialInstagram || selectedEmployee.socialWeb) && (
                    <div className="social-links-detail">
                      <h4>Connect</h4>
                      <div className="social-buttons">
                        {selectedEmployee.socialLinkedIn && (
                          <a href={selectedEmployee.socialLinkedIn} target="_blank" rel="noopener noreferrer" className="social-btn linkedin">
                            LinkedIn
                          </a>
                        )}
                        {selectedEmployee.socialTwitter && (
                          <a href={selectedEmployee.socialTwitter} target="_blank" rel="noopener noreferrer" className="social-btn twitter">
                            Twitter
                          </a>
                        )}
                        {selectedEmployee.socialInstagram && (
                          <a href={selectedEmployee.socialInstagram} target="_blank" rel="noopener noreferrer" className="social-btn instagram">
                            Instagram
                          </a>
                        )}
                        {selectedEmployee.socialWeb && (
                          <a href={selectedEmployee.socialWeb} target="_blank" rel="noopener noreferrer" className="social-btn website">
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowMyelinners; 